import { useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, Divider, FormControlLabel, Grid, IconButton,
    InputAdornment, Paper, Switch, TextField, Typography
} from "@mui/material";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

export default function TabZapSign() {
    const [openLoad, setOpenLoad] = useState(false);
    const [api_key_zapsign, setApiKeyZapSign] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [envioEmail, setEnvioEmail] = useState(true);
    const [envioWhatsapp, setEnvioWhatsapp] = useState(false);
    const [sandbox, setSandbox] = useState(false); // false = produção (padrão), true = teste/sandbox
    const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [testMsg, setTestMsg] = useState('');

    async function loadData() {
        setOpenLoad(true);
        try {
            const response = await api.get('/escritorio');
            const data = response.data;
            if (data) {
                const info = Array.isArray(data) ? data[0] : data;
                setApiKeyZapSign(info?.api_key_zapsign || '');
                setEnvioEmail(info?.zapsign_envio_email ? true : false);
                setEnvioWhatsapp(info?.zapsign_envio_whatsapp ? true : false);
                setSandbox(info?.zapsign_sandbox ? true : false);
                if (info?.api_key_zapsign) setTestStatus('idle');
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar dados' });
        }
        setOpenLoad(false);
    }

    async function handleTestar() {
        if (!api_key_zapsign.trim()) return AlertInfo({ icon: 'info', title: 'Informe o token da API antes de testar' });
        setOpenLoad(true);
        setTestStatus('idle');
        setTestMsg('');
        try {
            const response = await api.post('/testarzapsign', { api_key_zapsign });
            if (response.data.success) {
                setTestStatus('success');
                setTestMsg('Conexão realizada com sucesso! Token válido.');
            } else {
                setTestStatus('error');
                setTestMsg(response.data.msg || 'Token inválido ou sem acesso à ZapSign');
            }
        } catch (error) {
            setTestStatus('error');
            setTestMsg('Falha ao conectar com o servidor');
        }
        setOpenLoad(false);
    }

    async function handleSave() {
        if (!api_key_zapsign.trim()) return AlertInfo({ icon: 'info', title: 'Informe o token da API antes de salvar' });
        setOpenLoad(true);
        try {
            const response = await api.post('/salvarconfiguracaozapsign', {
                api_key_zapsign,
                zapsign_envio_email: envioEmail,
                zapsign_envio_whatsapp: envioWhatsapp,
                zapsign_sandbox: sandbox,
            });
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: 'Configuração ZapSign salva com sucesso!' });
                setTestStatus('success');
                setTestMsg('Token válido e salvo.');
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao salvar' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function handleRemover() {
        setOpenLoad(true);
        try {
            await api.post('/updateescritorio', { api_key_zapsign: null });
            setApiKeyZapSign('');
            setTestStatus('idle');
            AlertInfo({ icon: 'success', title: 'Token removido com sucesso' });
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            <SimpleBackdrop open={openLoad} />
            <Grid container spacing={2} justifyContent="center">

                {/* Card principal de configuração */}
                <Grid item xs={12} sm={9} md={7}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                            <KeyIcon color="primary" sx={{ fontSize: 30 }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                                    Integração ZapSign
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configuração do token da API para envio de documentos para assinatura eletrônica
                                </Typography>
                            </Box>
                            {api_key_zapsign ? (
                                <Chip label="CONFIGURADO" color="success" size="small" sx={{ ml: 'auto' }} />
                            ) : (
                                <Chip label="NÃO CONFIGURADO" color="warning" size="small" sx={{ ml: 'auto' }} />
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Alert severity="info" sx={{ mb: 3 }}>
                            Configure o token da sua conta <strong>ZapSign</strong> para enviar documentos
                            (contratos e declarações) para <strong>assinatura eletrônica</strong> direto do sistema.
                        </Alert>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Token da API ZapSign
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Cole aqui o token da API da ZapSign"
                                    value={api_key_zapsign}
                                    onChange={e => {
                                        setApiKeyZapSign(e.target.value);
                                        setTestStatus('idle');
                                    }}
                                    type={showKey ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowKey(!showKey)} edge="end" size="small">
                                                    {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: { fontFamily: 'monospace', fontSize: 13 }
                                    }}
                                />
                            </Grid>

                            {/* Ambiente (produção x teste/sandbox) */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Ambiente
                                </Typography>
                                <FormControlLabel
                                    control={<Switch color="warning" checked={sandbox} onChange={e => setSandbox(e.target.checked)} />}
                                    label={
                                        <Typography variant="body2">
                                            Modo teste (sandbox) — {sandbox ? 'ATIVO' : 'desativado'}
                                        </Typography>
                                    }
                                />
                                <Alert severity={sandbox ? 'warning' : 'success'} sx={{ mt: 0.5 }}>
                                    {sandbox
                                        ? 'Sandbox ativo: documentos são apenas de teste e não têm validade. Use enquanto não houver um Plano de API contratado.'
                                        : 'Produção: requer um Plano de API contratado na ZapSign. Documentos têm validade jurídica.'}
                                </Alert>
                            </Grid>

                            {/* Formas de envio do link de assinatura */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Formas de envio do link de assinatura
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    O link de assinatura é sempre exibido para você copiar. As opções abaixo liberam
                                    o envio automático aos signatários na hora de enviar o documento.
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                    <FormControlLabel
                                        control={<Switch checked={envioEmail} onChange={e => setEnvioEmail(e.target.checked)} />}
                                        label={
                                            <Box display="flex" alignItems="center" gap={0.8}>
                                                <EmailIcon fontSize="small" color="action" />
                                                <Typography variant="body2">Enviar por e-mail (gratuito)</Typography>
                                            </Box>
                                        }
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={envioWhatsapp} onChange={e => setEnvioWhatsapp(e.target.checked)} />}
                                        label={
                                            <Box display="flex" alignItems="center" gap={0.8}>
                                                <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                                                <Typography variant="body2">Enviar por WhatsApp (custo por envio cobrado pela ZapSign)</Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Grid>

                            {/* Resultado do teste */}
                            {testStatus !== 'idle' && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity={testStatus === 'success' ? 'success' : 'error'}
                                        icon={testStatus === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                                    >
                                        {testMsg}
                                    </Alert>
                                </Grid>
                            )}

                            {/* Botões de ação */}
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    color="info"
                                    onClick={handleTestar}
                                    disabled={!api_key_zapsign.trim()}
                                >
                                    TESTAR CONEXÃO
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={!api_key_zapsign.trim()}
                                >
                                    SALVAR
                                </Button>
                            </Grid>
                            {api_key_zapsign && (
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="error"
                                        onClick={handleRemover}
                                    >
                                        REMOVER TOKEN
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Card de instruções */}
                <Grid item xs={12} sm={9} md={7}>
                    <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Como obter seu token da API ZapSign
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1}>
                            {[
                                { step: '1', text: 'Acesse app.zapsign.com.br e faça login em sua conta' },
                                { step: '2', text: 'Clique em Configurações > Integrações' },
                                { step: '3', text: 'Acesse a seção ZapSign API' },
                                { step: '4', text: 'Copie o Token da API' },
                                { step: '5', text: 'Cole o token acima e clique em Testar Conexão' },
                                { step: '6', text: 'Se a conexão for OK, clique em Salvar' },
                            ].map(item => (
                                <Grid item xs={12} key={item.step}>
                                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                                        <Chip label={item.step} size="small" color="primary" sx={{ minWidth: 28, height: 24 }} />
                                        <Typography variant="body2">{item.text}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box mt={2}>
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open('https://app.zapsign.com.br/', '_blank')}
                            >
                                Abrir ZapSign
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

            </Grid>
        </>
    );
}
